/** RFC-0005 bounds, the same as the API schema. */
export const TAG_PART = /^[a-z0-9_-]{1,40}$/;
export const MAX_TAG_KEYS = 10;
export const MAX_TAG_VALUES = 20;

export type PolicyTags = Record<string, string[]>;

export interface TagInputResult {
  /** The tags with every accepted `key:value` added, in input order, without repeats. */
  tags: PolicyTags;
  /** Tokens that are not one `key:value` pair of valid parts. */
  invalid: string[];
  /** Valid tokens refused because they would pass a key or value limit. */
  overLimit: string[];
}

/** Splits on comma, space or new line; trims and lowercases each token. */
export function tagTokens(raw: string): string[] {
  return raw.split(/[\s,]+/).map((token) => token.trim().toLowerCase()).filter(Boolean);
}

function parseTag(token: string): [string, string] | null {
  const parts = token.split(":");
  if (parts.length !== 2) return null;
  const [key, value] = parts;
  return TAG_PART.test(key) && TAG_PART.test(value) ? [key, value] : null;
}

export function addTagInput(tags: PolicyTags, raw: string): TagInputResult {
  const next: PolicyTags = Object.fromEntries(Object.entries(tags).map(([key, values]) => [key, [...values]]));
  const invalid: string[] = [];
  const overLimit: string[] = [];
  for (const token of tagTokens(raw)) {
    const tag = parseTag(token);
    if (!tag) {
      invalid.push(token);
      continue;
    }
    const [key, value] = tag;
    const values = next[key];
    if (values?.includes(value)) continue;
    if (values ? values.length >= MAX_TAG_VALUES : Object.keys(next).length >= MAX_TAG_KEYS) {
      overLimit.push(token);
      continue;
    }
    next[key] = [...(values ?? []), value];
  }
  return { tags: next, invalid, overLimit };
}

/** Removes one value; a key with no value left leaves the tags, so it restricts nothing. */
export function removeTag(tags: PolicyTags, key: string, value: string): PolicyTags {
  const values = (tags[key] ?? []).filter((item) => item !== value);
  const { [key]: _removed, ...rest } = tags;
  return values.length > 0 ? { ...rest, [key]: values } : rest;
}
